import { useMemo, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  assignCategoryColors,
  formatCurrency,
  formatCompactCurrency,
  getCategoryColor,
  splitCategoryEmoji,
} from '@budget/core';
import type { AnnualIncome, DashboardData } from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { type Scope } from '../components/ScopeToggle';
import { useScope } from '../hooks/useScope';
import { PersonBreakdown } from '../components/PersonBreakdown';
import { CardHeader } from '../components/CardHeader';
import { JobsGanttChart } from '../components/JobsGanttChart';
import { DonutCenterEmoji } from '../components/DonutCenterEmoji';
import { ProgressFill } from '../components/ProgressFill';
import { BreakdownLegend } from '../components/BreakdownLegend';
import { BreakdownLegendGrid } from '../components/BreakdownLegendGrid';
import {
  StatGrid,
  StatItem,
  StatSubText,
} from '../components/StatBlock';
import { useChartReady } from '../hooks/useChartReady';
import { getPersonName } from '../config/personNames';
import clsx from 'clsx';

type View = 'current' | 'history' | 'jobs';

function fmt(n: number) {
  return formatCurrency(n);
}

function fmt0(n: number) {
  return formatCurrency(n, 'USD', 'en-US', 0);
}

function pickScope<T extends { household: number; dakota: number; natalia: number }>(
  t: T,
  scope: Scope
): number {
  return t[scope];
}

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  padding: '8px 12px',
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};

const TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };
const TOOLTIP_ITEM_STYLE = { color: 'var(--berg-text)' };

// Mirrors NetWorthHistoryChart's custom tooltip: name in white, value colored
// by series identity, label (year) in bold above. Keeps the income chart
// visually consistent with the net-worth chart.
interface IncomeTooltipItem {
  value?: number | string;
  name?: string;
  color?: string;
  dataKey?: string;
}

function IncomeChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: IncomeTooltipItem[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const items = payload.filter(
    (p) => p.value != null && p.value !== '' && Number.isFinite(Number(p.value)),
  );
  if (items.length === 0) return null;
  const colorFor = (item: IncomeTooltipItem): string => {
    if (item.dataKey === 'dakota') return 'var(--berg-dakota)';
    if (item.dataKey === 'natalia') return 'var(--berg-natalia)';
    if (item.dataKey === 'household') return 'var(--berg-green)';
    return item.color ?? 'var(--berg-text)';
  };
  return (
    <div style={{ ...TOOLTIP_STYLE, padding: '10px 14px' }}>
      <div style={{ ...TOOLTIP_LABEL_STYLE, marginBottom: 6 }}>{label}</div>
      {items.map((item, i) => (
        <div
          key={`${item.dataKey ?? item.name ?? i}`}
          style={{ marginTop: i === 0 ? 0 : 2 }}
        >
          <span style={{ color: 'var(--berg-text)', fontWeight: 500 }}>
            {item.name}:
          </span>{' '}
          <span style={{ color: colorFor(item) }}>
            {fmt(Number(item.value))}
          </span>
        </div>
      ))}
    </div>
  );
}

// Each tax type has a fixed identity color sourced from the shared
// CATEGORY_COLORS map. Sorted descending by value so the legend/donut/bar
// all render largest-first.
function getTaxBreakdown(income: DashboardData['income'], scope: Scope) {
  return [
    { label: 'Federal Taxes' as const, value: pickScope(income.taxes.federal, scope), color: getCategoryColor('Federal') },
    { label: 'FICA Taxes' as const, value: pickScope(income.taxes.fica, scope), color: getCategoryColor('FICA') },
    { label: 'State Taxes' as const, value: pickScope(income.taxes.state, scope), color: getCategoryColor('State') },
  ].sort((a, b) => b.value - a.value);
}

/* ===== Current view ===== */

function CurrentHero({
  income,
  scope,
}: {
  income: DashboardData['income'];
  scope: Scope;
}) {
  const gross = pickScope(income.grossAnnual, scope);
  const net = pickScope(income.netAnnual, scope);
  const taxes = pickScope(income.taxes.total, scope);
  const netMonthly = pickScope(income.netMonthly, scope);
  const grossMonthly = pickScope(income.grossMonthly, scope);
  const effectiveRate = gross > 0 ? (taxes / gross) * 100 : 0;

  const netPct = gross > 0 ? (net / gross) * 100 : 0;
  const taxBreakdown = getTaxBreakdown(income, scope);

  // Cross-element hover sync between the bar and its legend rows.
  const [hoveredBand, setHoveredBand] = useState<string | null>(null);
  const NET_KEY = 'take-home';

  const legendItems = [
    {
      key: NET_KEY,
      label: 'Take-home',
      value: net,
      color: getCategoryColor('Take-home'),
    },
    ...taxBreakdown.map((b) => ({
      key: b.label,
      label: b.label,
      value: b.value,
      color: b.color,
    })),
  ];

  return (
    <div className="card flex flex-col gap-5 h-full">
      <div className="flex flex-col gap-1">
        <span className="hero-eyebrow">Take-home this year</span>
        <div className="hero-balance tabular leading-none">{fmt0(net)}</div>
        <div className="mt-1">
          <PersonBreakdown
            dakota={income.netAnnual.dakota}
            natalia={income.netAnnual.natalia}
            digits={0}
          />
        </div>
      </div>

      <StatGrid columns={3} className="border-t border-border pt-4">
        <StatItem
          label="Net"
          value={fmt0(net)}
          sub={<StatSubText amount={fmt0(netMonthly)} unit="/mo" />}
        />
        <StatItem
          label="Gross"
          value={fmt0(gross)}
          sub={<StatSubText amount={fmt0(grossMonthly)} unit="/mo" />}
        />
        <StatItem
          label="Effective tax"
          valueTone="neg"
          value={`${effectiveRate.toFixed(1)}%`}
          sub={<StatSubText amount={fmt0(taxes)} />}
        />
      </StatGrid>

      <div className="flex flex-col gap-2">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-subtle gap-0.5">
          <ProgressFill
            pct={netPct}
            color={getCategoryColor('Take-home')}
            style={{
              opacity: hoveredBand !== null && hoveredBand !== NET_KEY ? 0.2 : 1,
            }}
            onMouseEnter={() => setHoveredBand(NET_KEY)}
            onMouseLeave={() => setHoveredBand(null)}
          />
          {taxBreakdown.map((b) => {
            const pct = gross > 0 ? (b.value / gross) * 100 : 0;
            const dimmed = hoveredBand !== null && hoveredBand !== b.label;
            return (
              <ProgressFill
                key={b.label}
                pct={pct}
                color={b.color}
                style={{ opacity: dimmed ? 0.2 : 1 }}
                onMouseEnter={() => setHoveredBand(b.label)}
                onMouseLeave={() => setHoveredBand(null)}
              />
            );
          })}
        </div>
        <div className="hidden sm:block">
          <BreakdownLegendGrid
            items={legendItems}
            total={gross}
            formatValue={fmt0}
            hovered={hoveredBand}
            onHoverChange={setHoveredBand}
          />
        </div>
        <div className="sm:hidden">
          <BreakdownLegend
            items={legendItems}
            total={gross}
            formatValue={fmt0}
            hovered={hoveredBand}
            onHoverChange={setHoveredBand}
            collapseAfter={Infinity}
          />
        </div>
      </div>
    </div>
  );
}

// Build the source breakdown used by both the donut card and the per-source
// cards. Sources are filtered by the active scope (so a person's view drops
// sources they don't earn) and sorted descending by value. Colors are
// assigned from the standard category palette with the income-summary
// semantic hues (Take-home green, Federal/FICA/State + their neighbors)
// excluded so no source slice visually masquerades as one of those buckets.
function useSourceBreakdown(
  income: DashboardData['income'],
  scope: Scope,
) {
  return useMemo(() => {
    // Keep every source in the list so the donut + legend rows stay stable
    // when switching scopes — entries the active scope doesn't earn fall
    // through as $0 instead of disappearing. Sort by the active scope's value
    // so the legend reads largest-first for whichever person is being viewed.
    const ordered = [...income.sources].sort(
      (a, b) => pickScope(b, scope) - pickScope(a, scope),
    );
    const colors = assignCategoryColors(
      ordered.map((s) => s.name),
      [
        getCategoryColor('Take-home'),
        'var(--berg-green-bold)',
        getCategoryColor('Federal'),
        getCategoryColor('FICA'),
        getCategoryColor('State'),
        'var(--berg-red-bold)',
        'var(--berg-orange-bold)',
        'var(--berg-amber)',
        'var(--berg-amber-light)',
      ],
    );
    return ordered.map((s) => ({
      source: s,
      label: s.name,
      value: pickScope(s, scope),
      color: colors.get(s.name) ?? 'var(--berg-text-tertiary)',
    }));
  }, [income.sources, scope]);
}

function SourcesDonutCard({
  income,
  scope,
}: {
  income: DashboardData['income'];
  scope: Scope;
}) {
  const total = pickScope(income.grossMonthly, scope);
  const breakdown = useSourceBreakdown(income, scope);

  const [hovered, setHoveredRaw] = useState<string | null>(null);
  // Hover-dim is suppressed during the entry animation so the donut's
  // mount swoop-in doesn't get canceled when the cursor is already on it.
  const ready = useChartReady();
  const setHovered = (next: string | null) => {
    if (ready) setHoveredRaw(next);
  };

  return (
    <div className="card">
      <CardHeader
        title="Income sources"
        subtitle={
          <>
            Monthly gross ·{' '}
            <span className="tabular text-fg-secondary">{fmt0(total)}</span>
            <span className="ml-0.5">/mo</span>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="relative h-[220px]">
          <DonutCenterEmoji
            emoji={hovered ? splitCategoryEmoji(hovered).emoji : null}
          />
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={breakdown}
                dataKey="value"
                animationDuration={1000}
                nameKey="label"
                innerRadius="60%"
                outerRadius="98%"
                startAngle={90}
                endAngle={-270}
                paddingAngle={0.5}
                stroke="var(--berg-bg-elevated)"
                strokeWidth={1}
                onMouseEnter={(_, index) => setHovered(breakdown[index]?.label ?? null)}
                onMouseLeave={() => setHovered(null)}
              >
                {breakdown.map((b) => (
                  <Cell
                    key={b.label}
                    fill={b.color}
                    fillOpacity={hovered === null || hovered === b.label ? 1 : 0.2}
                    style={{ transition: 'fill-opacity 0.18s ease' }}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(v) => fmt(typeof v === 'number' ? v : 0)}
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                cursor={{ fill: 'transparent' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-col text-sm">
          {breakdown.map((b) => {
            const pct = total > 0 ? (b.value / total) * 100 : 0;
            return (
              <div
                key={b.label}
                onMouseEnter={() => setHovered(b.label)}
                onMouseLeave={() => setHovered(null)}
                className="flex items-center justify-between gap-3 px-2 py-1.5 -mx-2 rounded-md cursor-default"
                style={{
                  backgroundColor:
                    hovered === b.label ? 'var(--berg-overlay)' : 'transparent',
                  opacity: hovered === null || hovered === b.label ? 1 : 0.4,
                  transition: 'opacity 0.18s ease, background-color 0.15s ease',
                }}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full flex-none dot-glow"
                    style={
                      { backgroundColor: b.color, '--dot-color': b.color } as CSSProperties
                    }
                  />
                  <span className="truncate text-fg">{b.label}</span>
                </div>
                <div className="flex items-center gap-3 flex-none tabular">
                  <span className="font-semibold">{fmt0(b.value)}</span>
                  <span className="text-fg-tertiary text-xs w-12 text-right">
                    {pct.toFixed(1)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Donut + legend pair for a single source card. Lives in its own component
// so each card owns local hover state — hovering a slice dims its sibling
// and highlights the matching legend row, and vice versa.
function SourceDonut({
  donutData,
  annual,
}: {
  donutData: { key: string; label: string; value: number; color: string }[];
  annual: number;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  return (
    <div className="border-t border-border pt-3 flex flex-col items-stretch gap-3">
      <div className="relative h-[140px] mx-auto w-[140px] flex-none">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={donutData}
              dataKey="value"
              nameKey="label"
              innerRadius="60%"
              outerRadius="98%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={0.5}
              stroke="var(--berg-bg-elevated)"
              strokeWidth={1}
              isAnimationActive={false}
              onMouseEnter={(_, idx) => setHovered(donutData[idx]?.key ?? null)}
              onMouseLeave={() => setHovered(null)}
            >
              {donutData.map((d) => (
                <Cell
                  key={d.key}
                  fill={d.color}
                  fillOpacity={hovered === null || hovered === d.key ? 1 : 0.2}
                  style={{ transition: 'fill-opacity 0.18s ease' }}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(v) => fmt0(typeof v === 'number' ? v : 0)}
              contentStyle={TOOLTIP_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
              cursor={{ fill: 'transparent' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <BreakdownLegendGrid
        items={donutData}
        total={annual}
        formatValue={fmt0}
        columns={2}
        mobileColumns={2}
        hovered={hovered}
        onHoverChange={setHovered}
      />
    </div>
  );
}

// Per-source cards. Mirrors the donut's breakdown (same color assignment,
// same scope filtering) so a card's dot always matches its slice.
function SourceCards({
  income,
  scope,
}: {
  income: DashboardData['income'];
  scope: Scope;
}) {
  // Donut + legend keep zero-value sources so the layout stays stable when
  // switching scopes, but a per-source $0 card has nothing to show — drop
  // those here.
  const breakdown = useSourceBreakdown(income, scope).filter((b) => b.value > 0);
  const grossMonthly = pickScope(income.grossMonthly, scope);
  const taxesColor = getCategoryColor('Taxes');

  if (breakdown.length === 0) return null;

  return (
    <div className="grid grid-cols-3 gap-5 max-md:grid-cols-2 max-sm:grid-cols-1">
      {breakdown.map(({ source, label, value, color }) => {
        const annual = value * 12;
        const pct = grossMonthly > 0 ? (value / grossMonthly) * 100 : 0;
        const onlyDakota = source.dakota > 0 && source.natalia === 0;
        const onlyNatalia = source.natalia > 0 && source.dakota === 0;
        // Per-source taxes come straight from the sheet's "Taxes" column
        // (household monthly). For a per-person scope, apportion by that
        // person's share of the source's gross so split-source taxes follow
        // the same split as the income.
        const scopeShareOfSource =
          source.household > 0 ? pickScope(source, scope) / source.household : 0;
        const sourceTaxes = source.monthlyTaxes * 12 * scopeShareOfSource;
        const sourceTakeHome = annual - sourceTaxes;
        // Take-home slice picks up the earning person's color when the source
        // is attributable to one person — either because the active scope is
        // that person, or (under household scope) only one of the two has any
        // share of this source. Sources split across both people fall back to
        // the joint take-home green.
        const takeHomeColor =
          scope === 'dakota' || (scope === 'household' && onlyDakota)
            ? 'var(--berg-dakota)'
            : scope === 'natalia' || (scope === 'household' && onlyNatalia)
              ? 'var(--berg-natalia)'
              : getCategoryColor('Take-home');
        const donutData = [
          { key: 'take-home', label: 'Take-home', value: sourceTakeHome, color: takeHomeColor },
          { key: 'taxes', label: 'Taxes', value: sourceTaxes, color: taxesColor },
        ];
        return (
          <div key={label} className="card flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-none dot-glow"
                  style={
                    {
                      background: color,
                      '--dot-color': color,
                    } as CSSProperties
                  }
                />
                <span className="text-sm text-fg-secondary truncate">{label}</span>
              </div>
              <span className="text-sm text-fg-tertiary tabular flex-none">
                {pct.toFixed(0)}%
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="text-2xl font-semibold tabular leading-none">
                {fmt0(annual)}
                <span className="text-base text-fg-tertiary font-normal ml-1">/yr</span>
              </div>
              <div className="text-sm text-fg-tertiary tabular">
                {fmt0(value)}/mo
              </div>
            </div>
            {annual > 0 && sourceTaxes > 0 && (
              <SourceDonut donutData={donutData} annual={annual} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ===== History view ===== */

function HeroSummary({ rows, scope }: { rows: AnnualIncome[]; scope: Scope }) {
  if (rows.length === 0) return null;

  const meaningful = rows.filter((r) => pickScope(r, scope) > 0);
  const latest = meaningful[meaningful.length - 1] ?? rows[rows.length - 1];
  const prior = meaningful[meaningful.length - 2];
  const latestValue = pickScope(latest, scope);
  const priorValue = prior ? pickScope(prior, scope) : 0;
  const delta = prior ? latestValue - priorValue : 0;
  const deltaPct = prior && priorValue > 0 ? (delta / priorValue) * 100 : 0;

  const lifetime = rows.reduce(
    (acc, r) => ({
      dakota: acc.dakota + r.dakota,
      natalia: acc.natalia + r.natalia,
      household: acc.household + r.household,
    }),
    { dakota: 0, natalia: 0, household: 0 }
  );
  const lifetimeValue = pickScope(lifetime, scope);

  const positive = delta >= 0;

  return (
    <div className="grid grid-cols-2 gap-5 max-md:grid-cols-1">
      <div className="card flex flex-col gap-3 h-full min-w-0">
        <div className="flex flex-col gap-1">
          <span className="hero-eyebrow">Earning in {latest.year}</span>
          <div className="hero-balance tabular leading-none">{fmt0(latestValue)}</div>
          {prior && (
            <div className={clsx('hero-delta mt-1', positive ? 'delta-pos' : 'delta-neg')}>
              <span aria-hidden>{positive ? '▲' : '▼'}</span>
              <span className="tabular">
                {positive ? '+' : ''}
                {fmt0(delta)}
                {priorValue > 0 && (
                  <span className="ml-1 text-fg-tertiary font-normal">
                    ({deltaPct >= 0 ? '+' : ''}
                    {deltaPct.toFixed(1)}%) vs {prior.year}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        <PersonBreakdown dakota={latest.dakota} natalia={latest.natalia} digits={0} />
      </div>
      <div className="card flex flex-col gap-3 h-full min-w-0">
        <div className="flex flex-col gap-1">
          <span className="hero-eyebrow">Lifetime earnings</span>
          <div className="hero-balance tabular leading-none">{fmt0(lifetimeValue)}</div>
          <div className="text-[0.9375rem] text-fg-tertiary mt-1">
            {rows.length} years on record ({rows[0]?.year}–{rows[rows.length - 1]?.year})
          </div>
        </div>
        <PersonBreakdown dakota={lifetime.dakota} natalia={lifetime.natalia} digits={0} />
      </div>
    </div>
  );
}

function IncomeChart({ rows, scope }: { rows: AnnualIncome[]; scope: Scope }) {
  const data = useMemo(
    () =>
      rows.map((r) => ({
        year: r.year,
        dakota: r.dakota,
        natalia: r.natalia,
        household: r.household,
      })),
    [rows]
  );

  if (rows.length === 0) {
    return <div className="card empty-state">No income history found in the sheet.</div>;
  }

  return (
    <div className="card">
      <CardHeader title="Annual income" />

      <div className="h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="grad-dakota" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--berg-dakota)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--berg-dakota)" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-natalia" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--berg-natalia)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--berg-natalia)" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-household" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--berg-green)" stopOpacity={0.5} />
                <stop offset="100%" stopColor="var(--berg-green)" stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <CartesianGrid stroke="var(--berg-border)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
              axisLine={false}
              tickLine={false}
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
              tickFormatter={(v: number) => formatCompactCurrency(v)}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip
              content={<IncomeChartTooltip />}
              cursor={{ stroke: 'var(--berg-border-strong)', strokeWidth: 1 }}
            />

            {scope === 'household' ? (
              <>
                <Area
                  type="monotone"
                  dataKey="household"
                  name="Household"
                  stroke="var(--berg-green)"
                  strokeWidth={2}
                  fill="url(#grad-household)"
                />
                <Area
                  type="monotone"
                  dataKey="dakota"
                  name={getPersonName('dakota')}
                  stroke="var(--berg-dakota)"
                  strokeWidth={2}
                  fill="url(#grad-dakota)"
                />
                <Area
                  type="monotone"
                  dataKey="natalia"
                  name={getPersonName('natalia')}
                  stroke="var(--berg-natalia)"
                  strokeWidth={2}
                  fill="url(#grad-natalia)"
                />
              </>
            ) : (
              <Area
                type="monotone"
                dataKey={scope}
                name={getPersonName(scope)}
                stroke={scope === 'dakota' ? 'var(--berg-dakota)' : 'var(--berg-natalia)'}
                strokeWidth={2.5}
                fill={`url(#grad-${scope})`}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Strip parenthetical phrases from a source name, e.g. "Anthropic (Claude)"
// becomes "Anthropic". Sheet authors use parens for inline disambiguators
// that read as noise inside the compact table cells.
function stripParens(s: string | undefined): string | undefined {
  if (!s) return s;
  return s.replace(/\s*\([^)]*\)/g, '').trim();
}

function YearsTable({ rows, scope }: { rows: AnnualIncome[]; scope: Scope }) {
  const sorted = [...rows].reverse();

  // Lookup table for per-year prior so we can compute YoY deltas.
  const byYear = new Map<number, AnnualIncome>();
  rows.forEach((r) => byYear.set(r.year, r));

  // Returns the % change from prev to curr, or null when undefined / prev is 0.
  function deltaPct(curr: number, prev: number | undefined): number | null {
    if (prev === undefined || prev === 0) return null;
    return ((curr - prev) / Math.abs(prev)) * 100;
  }

  function DeltaTag({ pct, className }: { pct: number | null; className?: string }) {
    if (pct === null) return null;
    return (
      <span className={clsx('tabular text-xs text-fg-tertiary', className)}>
        {pct >= 0 ? '+' : ''}
        {pct.toFixed(1)}%
      </span>
    );
  }

  // Desktop dims out the non-active person's column when scoped to one
  // person rather than removing it — keeps the table layout stable and
  // still surfaces the household total. Mobile cards (further down) still
  // do strict scope-based hiding since horizontal real estate is tight.
  const dimDakota = scope === 'natalia';
  const dimNatalia = scope === 'dakota';
  const showDakota = scope === 'dakota' || scope === 'household';
  const showNatalia = scope === 'natalia' || scope === 'household';

  return (
    <div className="data-table">
      <CardHeader flush title="Year by year" />
      <table className="max-md:hidden">
        <thead>
          <tr className="text-left">
            <th>Year</th>
            <th className={clsx(dimDakota && 'opacity-40')}>
              <span className="inline-flex items-center gap-1.5">
                <span className="person-dot dot-dakota" />
                {getPersonName('dakota')}
              </span>
            </th>
            <th className={clsx(dimNatalia && 'opacity-40')}>
              <span className="inline-flex items-center gap-1.5">
                <span className="person-dot dot-natalia" />
                {getPersonName('natalia')}
              </span>
            </th>
            <th>Household</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const prior = byYear.get(r.year - 1);
            const dakotaDelta = deltaPct(r.dakota, prior?.dakota);
            const nataliaDelta = deltaPct(r.natalia, prior?.natalia);
            const householdDelta = deltaPct(r.household, prior?.household);
            return (
              <tr key={r.year}>
                <td className="font-semibold tabular">{r.year}</td>
                <td className={clsx('text-dakota tabular', dimDakota && 'opacity-40')}>
                  <div className="flex items-baseline">
                    <span>{fmt0(r.dakota)}</span>
                    <DeltaTag pct={dakotaDelta} className="ml-2" />
                  </div>
                  {r.dakotaSource && (
                    <div className="text-fg-tertiary text-xs mt-0.5 truncate max-w-[280px]">
                      {stripParens(r.dakotaSource)}
                    </div>
                  )}
                </td>
                <td className={clsx('text-natalia tabular', dimNatalia && 'opacity-40')}>
                  <div className="flex items-baseline">
                    <span>{fmt0(r.natalia)}</span>
                    <DeltaTag pct={nataliaDelta} className="ml-2" />
                  </div>
                  {r.nataliaSource && (
                    <div className="text-fg-tertiary text-xs mt-0.5 truncate max-w-[280px]">
                      {stripParens(r.nataliaSource)}
                    </div>
                  )}
                </td>
                <td className="tabular font-semibold">
                  <div className="inline-flex items-baseline">
                    <span>{fmt0(r.household)}</span>
                    <DeltaTag pct={householdDelta} className="ml-2" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="hidden max-md:block">
        {sorted.map((r) => {
          const prior = byYear.get(r.year - 1);
          const dakotaDelta = deltaPct(r.dakota, prior?.dakota);
          const nataliaDelta = deltaPct(r.natalia, prior?.natalia);
          const householdDelta = deltaPct(r.household, prior?.household);
          return (
            <div
              key={r.year}
              className="border-t border-border first:border-t-0 px-4 py-3 flex flex-col gap-2.5"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-base font-semibold tabular">{r.year}</span>
                <div className="flex items-baseline gap-2 shrink-0 tabular">
                  <span className="text-base font-semibold text-right min-w-[5rem]">
                    {fmt0(r.household)}
                  </span>
                  <span className="text-xs text-fg-tertiary text-right min-w-[3.25rem]">
                    {householdDelta != null
                      ? `${householdDelta >= 0 ? '+' : ''}${householdDelta.toFixed(1)}%`
                      : ''}
                  </span>
                </div>
              </div>
              {(showDakota || showNatalia) && (
                <div className="flex flex-col gap-1.5">
                  {showDakota && (
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="person-dot dot-dakota" />
                        <span className="text-fg-secondary truncate">
                          {stripParens(r.dakotaSource) || getPersonName('dakota')}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 shrink-0 tabular">
                        <span className="text-dakota text-right min-w-[5rem]">
                          {fmt0(r.dakota)}
                        </span>
                        <span className="text-xs text-fg-tertiary text-right min-w-[3.25rem]">
                          {dakotaDelta != null
                            ? `${dakotaDelta >= 0 ? '+' : ''}${dakotaDelta.toFixed(1)}%`
                            : ''}
                        </span>
                      </div>
                    </div>
                  )}
                  {showNatalia && (
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="person-dot dot-natalia" />
                        <span className="text-fg-secondary truncate">
                          {stripParens(r.nataliaSource) || getPersonName('natalia')}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 shrink-0 tabular">
                        <span className="text-natalia text-right min-w-[5rem]">
                          {fmt0(r.natalia)}
                        </span>
                        <span className="text-xs text-fg-tertiary text-right min-w-[3.25rem]">
                          {nataliaDelta != null
                            ? `${nataliaDelta >= 0 ? '+' : ''}${nataliaDelta.toFixed(1)}%`
                            : ''}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===== Page ===== */

export function IncomeHistoryPage() {
  const { data, isLoading, error } = useDashboard();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const view: View =
    tabParam === 'history' ? 'history' : tabParam === 'jobs' ? 'jobs' : 'current';
  const setView = (next: View) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'current') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const { scope } = useScope();

  return (
    <div className="page-container space-y-5">
      <header>
        <div className="page-header">
          <div>
            <h1 className="page-title">Income</h1>
            <p className="page-subtitle">
              {view === 'current'
                ? 'Where your income comes from right now'
                : view === 'history'
                  ? 'Annual earnings since 2008'
                  : 'Every job, plotted on a timeline'}
            </p>
          </div>
        </div>
      </header>
      <div className="tab-row max-md:tab-row-sticky max-md:-mt-5">
        <div className="page-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={view === 'current'}
            onClick={() => setView('current')}
            className={clsx('page-tab', view === 'current' && 'page-tab-active')}
          >
            Current
          </button>
          <button
            role="tab"
            aria-selected={view === 'history'}
            onClick={() => setView('history')}
            className={clsx('page-tab', view === 'history' && 'page-tab-active')}
          >
            History
          </button>
          <button
            role="tab"
            aria-selected={view === 'jobs'}
            onClick={() => setView('jobs')}
            className={clsx('page-tab', view === 'jobs' && 'page-tab-active')}
          >
            Jobs
          </button>
        </div>
      </div>

      {error && <div className="alert-error">{error}</div>}

      {!data && isLoading && (
        <div className="card text-center py-12 text-fg-secondary">Loading…</div>
      )}

      {data && view === 'current' && (
        <>
          <div className="grid grid-cols-3 gap-5 max-md:grid-cols-1">
            <div className="col-span-2 max-md:col-span-1">
              <CurrentHero income={data.income} scope={scope} />
            </div>
            <SourcesDonutCard income={data.income} scope={scope} />
          </div>
          <SourceCards income={data.income} scope={scope} />
        </>
      )}

      {data && view === 'history' && (
        <>
          <HeroSummary rows={data.incomeHistory} scope={scope} />
          <IncomeChart rows={data.incomeHistory} scope={scope} />
          <YearsTable rows={data.incomeHistory} scope={scope} />
        </>
      )}

      {data && view === 'jobs' && (
        <JobsGanttChart jobs={data.jobs} scope={scope} />
      )}
    </div>
  );
}
